import axios from 'axios';
import { getCurrentAuthToken } from 'Utils/Auth';
import { claimKeepaliveChars, keepaliveCharsAvailable } from 'Models/keepaliveBudget';
import type { ScaleCell, ScaleConfig } from 'Types/ExerciseContentTypes';
import type { ScaleProgressCell } from 'Types/ExerciseProgressTypes';

/**
 * Client for the exercise-progress HTTP surface: the sitting's open and close, the
 * earned clear and the frontier read. Its only caller - the 7c saga - stays dark
 * behind USE_EXERCISES.
 *
 * Follows the house saga/API idiom (see `Sagas/Repertoire.ts`): axios against
 * `${REACT_APP_BACKEND_URL}/api/v1/`, no `user_id` in the body (the backend derives
 * it from the authenticated request).
 */

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;

/**
 * The earned-clear write body. Mirrors the backend `RecordClearDto` MINUS `user_id`
 * (auth-derived) and MINUS `cleared_at`: the "when cleared" timestamp is server-stamped
 * at transaction time. A server-authoritative store must not trust a client wall-clock,
 * so MUS-458 stamps it, period. `play_session_id` is optional and stays null until the
 * play-session lifecycle endpoint lands (also MUS-458).
 */
export interface ExerciseClearRequest {
  exercise_id: number;
  scale: ScaleCell['scale'];
  tonic: ScaleCell['tonic'];
  hand: ScaleConfig['hand'];
  range_octaves: number;
  direction: ScaleConfig['direction'];
  rhythm_density: ScaleConfig['rhythm_density'];
  tempo: number;
  mastered: boolean;
  play_session_id?: string | null;
  // MUS-459 - the app-session analytics id, threaded onto the clear so the backend's
  // point-earned + tier-completed emits carry a NOT-NULL analytics_session_id.
  analytics_session_id?: string | null;
  /** The same on every resend of one clear: the server stores it once (7-day window). */
  client_event_id?: string;
}

/** The field errors a 422 carries, e.g. `{ play_session_id: 'ExercisePlaySessionNotOpened' }`. */
export interface FieldErrors {
  errors?: Record<string, string>;
}

/** The server refused the write because it names a sitting the open has not minted (yet). */
export const SITTING_NOT_OPENED = 'ExercisePlaySessionNotOpened';

/**
 * POST an earned clear. Returns the cell's canonical frontier AFTER the backend's
 * domination/prune, so the slice takes what this returns rather than guessing
 * (server-authoritative).
 */
export async function postExerciseClear(body: ExerciseClearRequest): Promise<ScaleProgressCell> {
  const res = await axios.post(`${baseUrl}exercise-progress/clears`, body);
  return res.data as ScaleProgressCell;
}

/** GET the per-cell frontier for the authenticated user and one exercise. */
export async function getExerciseProgress(exerciseId: number): Promise<ScaleProgressCell[]> {
  const res = await axios.get(`${baseUrl}exercise-progress`, {
    params: { exercise_id: exerciseId },
  });
  return res.data as ScaleProgressCell[];
}

/**
 * The open-a-sitting body for POST /exercise-play-session (MUS-459). `user_id` is
 * auth-derived server-side, never sent. The backend mints the row and emits the
 * session-started event; it returns the minted play_session_id.
 */
export interface OpenPlaySessionRequest {
  exercise_id: number;
  analytics_session_id: string;
  datetime: string;
  platform?: string;
  /** The level and tier the sitting was started from; the server checks the tier exists. */
  entry_context?: { level: number; tier: number } | null;
}

/**
 * Open the sitting. Retries are disabled on this POST ('axios-retry' retries: 0): a
 * lost-response retry would mint a SECOND sitting, so the saga owns the one retry and
 * keys it on not-yet-holding an id (call open once per sitting).
 */
export async function openPlaySession(body: OpenPlaySessionRequest): Promise<string> {
  const NO_RETRY = {
    'axios-retry': { retries: 0, retryCondition: () => false } as {
      retries: number;
      retryCondition: () => boolean;
    },
  };
  const res = await axios.post(`${baseUrl}exercise-play-session`, body, NO_RETRY);
  return (res.data as { play_session_id: string }).play_session_id;
}

/** What the client counted in the sitting, sent on the close and stored as reported. */
export interface PlaySessionSummary {
  playthroughs: number;
  verdict_counts: { failed: number; clean: number; flawless: number };
  /** The bars met in the sitting. */
  points_earned: number;
}

/** The close-a-sitting body for POST /exercise-play-session/:id/close (MUS-459). */
export interface ClosePlaySessionRequest {
  analytics_session_id: string;
  datetime: string;
  platform?: string;
  summary?: PlaySessionSummary;
}

/**
 * Close the sitting: the backend persists the telemetry aggregate, keeps the summary and emits
 * the tier-session-finished event. Idempotent server-side (a re-close changes nothing).
 *
 * Sent over keepalive, because a sitting is also closed when the page is hidden for good or the
 * play screen unmounts, and a keepalive request outlives the page. Rejects when the request fails
 * or the server answers with an error.
 */
export async function closePlaySession(
  playSessionId: string,
  body: ClosePlaySessionRequest,
): Promise<void> {
  const text = JSON.stringify(body);
  const keepalive = text.length <= keepaliveCharsAvailable();
  const release = keepalive ? claimKeepaliveChars(text.length) : () => {};
  try {
    let token: string | undefined;
    try {
      token = await getCurrentAuthToken();
    } catch {
      token = undefined;
    }
    const response = await fetch(
      `${baseUrl}exercise-play-session/${encodeURIComponent(playSessionId)}/close`,
      {
        method: 'POST',
        keepalive,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: text,
      },
    );
    if (!response.ok) {
      throw new Error(`play-session close answered ${response.status}`);
    }
  } finally {
    release();
  }
}
